import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner, Avatar, Progress, Tooltip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { Typography, UniversalTable } from "../../../../components";
import IdView from "../../../../components/id-view";
import { CalendarIcon } from "../../../../components/icons/ComplexIcons";
import { useTranslations } from "../../../../contexts/translation.context";
import { formatTimeAgo } from "../../../../utils/formatTimeAgo";
import { CPLOrder } from "../../../../api/staff";
import { EmptyContentIc, InfoCircle } from "../../../../components/icons";

interface CPLOrdersTableProps {
  orders: CPLOrder[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onDeactivate?: (orderId: string) => void;
}

/**
 * CPLOrdersTable Component
 *
 * Main table component for displaying CPL orders.
 * Uses UniversalTable from the component library with custom cell renderers.
 * Updated to match the reference implementation from onlyu_front/src/partner/subscribers/orders
 *
 * Table structure:
 * - ID: Order ID with copy functionality
 * - Model: Model avatar, name, username, type, platform, offer info
 * - Offer: Progress bar, statistics (price, messages, transactions, subscribers), ARPU, ROMI
 * - Status: Status badge with color coding
 * - Created At: Date with time ago format
 */
const CPLOrdersTable: React.FC<CPLOrdersTableProps> = ({
  orders,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onDeactivate,
}) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  // Define table columns matching onlyu_front structure
  const columns = [
    {
      key: "id",
      name: "ID",
      sortable: false
    },
    {
      key: "model",
      name: t('admin.cplOrders.table.model'),
      sortable: false
    },
    {
      key: "partner",
      name: t('admin.cplOrders.table.partner'),
      sortable: false
    },
    {
      key: "status",
      name: t('admin.cplOrders.table.status'),
      sortable: false
    },
    {
      key: "statistics",
      name: t('admin.cplOrders.table.statistics'),
      sortable: false
    },
    {
      key: "revenue",
      name: t('admin.cplOrders.table.revenue'),
      sortable: false
    },
    {
      key: "createdAt",
      name: t('admin.cplOrders.table.createdAt'),
      sortable: false
    },
    {
      key: "actions",
      name: "",
      sortable: false
    }
  ];

  // Status color configuration
  const getStatusColor = (status: CPLOrder['status']) => {
    switch (status) {
      case 'in_progress':
        return 'bg-blue-600';
      case 'waiting':
        return 'bg-yellow-600';
      case 'completed':
        return 'bg-green-600';
      case 'canceled':
        return 'bg-red-600';
      case 'deactivated':
        return 'bg-gray-600';
      default:
        return 'bg-gray-600';
    }
  };

  // Get status label
  const getStatusLabel = (status: CPLOrder['status']) => {
    switch (status) {
      case 'in_progress':
        return t('admin.cplOrders.status.inProgress');
      case 'waiting':
        return t('admin.cplOrders.status.waiting');
      case 'completed':
        return t('admin.cplOrders.status.completed');
      case 'canceled':
        return t('admin.cplOrders.status.canceled');
      case 'deactivated':
        return t('admin.cplOrders.status.deactivated');
      default:
        return status;
    }
  };

  // Calculate ARPU (Average Revenue Per User)
  const calculateARPU = (order: CPLOrder): number => {
    const totalRevenue = parseFloat(order.transactions_statistic?.total || '0') || 0;
    const subscribers = order.subscribed_count || 0;
    return subscribers > 0 ? totalRevenue / subscribers : 0;
  };

  // Calculate ROMI (Return on Marketing Investment)
  const calculateROMI = (order: CPLOrder): number => {
    const totalRevenue = parseFloat(order.transactions_statistic?.total || '0') || 0;
    const totalCost = parseFloat(order.subs?.price || '0') * (order.subscribed_count || 0);
    return totalCost > 0 ? ((totalRevenue - totalCost) / totalCost) * 100 : 0;
  };

  // Cell renderer - main rendering logic for each cell
  const renderCell = useCallback((order: CPLOrder, columnKey: string) => {
    switch (columnKey) {
      case "id":
        // ID Cell: Shows short ID with copy functionality
        return (
          <div className="flex flex-col items-start gap-1">
            <IdView textToCopy={order._id}>
              {order._id.slice(-4)}
            </IdView>
          </div>
        );

      case "model":
        // Model Cell: Avatar + name with verification icon
        const renderModelTooltip = () => {
          const ofData = order.model?.of_data;

          const parseNumber = (value: unknown) => {
            if (value === null || value === undefined) return 0;
            if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
            const numericValue = Number(value);
            return Number.isFinite(numericValue) ? numericValue : 0;
          };

          const formatCurrency = (value: unknown) => `$${parseNumber(value).toFixed(2)}`;
          const formatInteger = (value: unknown) => parseNumber(value).toLocaleString();

          const balance = formatCurrency(ofData?.balance);
          const subscribers = formatInteger(ofData?.subscribers ?? ofData?.subscribers_count);
          const favorited = formatInteger(ofData?.favorited ?? ofData?.likes_count);
          const posts = formatInteger(ofData?.posts ?? ofData?.posts_count);
          const blockedCountriesCount = Array.isArray(order.model?.blocked_countries)
            ? order.model?.blocked_countries?.length || 0
            : 0;
          const blockedCountries = blockedCountriesCount.toLocaleString();

          return (
            <div className="bg-white border border-[#EDEDED] rounded-[12px] p-0 min-w-[280px]">
              {/* Header */}
              <div className="px-[16px] pt-[16px] pb-0">
                <Typography variant="body2" className="text-[#11181C] text-[14px] font-normal leading-[18px]">
                  Общая информация
                </Typography>
              </div>

              {/* Stats */}
              <div className="flex flex-col gap-[4px] px-[16px] py-0">
                {/* Balance */}
                <div className="flex items-center justify-between py-[6px]">
                  <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
                    Баланс
                  </Typography>
                  <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
                    {balance}
                  </Typography>
                </div>

                {/* Subscribers */}
                <div className="flex items-center justify-between py-[6px]">
                  <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
                    Подписчики
                  </Typography>
                  <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
                    {subscribers}
                  </Typography>
                </div>

                {/* Favorited */}
                <div className="flex items-center justify-between py-[6px]">
                  <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
                    Лайки
                  </Typography>
                  <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
                    {favorited}
                  </Typography>
                </div>

                {/* Posts */}
                <div className="flex items-center justify-between py-[6px]">
                  <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
                    Посты
                  </Typography>
                  <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
                    {posts}
                  </Typography>
                </div>

                {/* Blocked countries */}
                <div className="flex items-center justify-between py-[6px]">
                  <Typography variant="body3" className="text-[#888B8D] text-[12px] font-normal leading-[16px]">
                    Заблокированные страны
                  </Typography>
                  <Typography variant="body3" className="text-[#3F3F46] text-[12px] font-medium leading-[16px]">
                    {blockedCountries}
                  </Typography>
                </div>
              </div>

              {/* Footer */}
              <div className="px-[16px] pb-[16px]">
                <Typography variant="body4" className="text-[#888B8D] text-[10px] font-normal leading-[16px]">
                  {ofData?.updated_at ? `Обновлено: ${new Date(ofData.updated_at).toLocaleString('ru-RU')}` : 'Нет данных об обновлении'}
                </Typography>
              </div>
            </div>
          );
        };

        return (
          <div className="flex items-center gap-3">
            <Avatar
              src={order.model?.avatar}
              showFallback
              name={order.model?.name ? order.model.name.slice(0, 1) : '?'}
              className="min-w-[40px] h-[40px] rounded-[12px] bg-gray-200"
              classNames={{
                fallback: "bg-gray-200 text-gray-600"
              }}
            />
            <div className="flex items-center gap-[4px]">
              <Typography
                variant="label2"
                className="whitespace-nowrap leading-[20px] !text-[#006FEE] cursor-pointer hover:underline"
                onClick={() => {
                  if (order.model?.of_user_name) {
                    window.open(`https://onlyfans.com/${order.model.of_user_name}`, '_blank');
                  }
                }}
              >
                {order.model?.name || 'Unknown'}
              </Typography>
              <Tooltip
                content={renderModelTooltip()}
                placement="bottom"
                showArrow={false}
                classNames={{
                  content: "p-0 bg-transparent shadow-none",
                }}
              >
                <div className="cursor-pointer">
                  <InfoCircle/>
                </div>
              </Tooltip>
            </div>
          </div>
        );

      case "partner":
        // Partner Cell: Partner name with badge (Agency/Creator)
        // @ts-ignore
        const ownerType = order?.owner_type || 'creator'; // Default to 'creator' if not provided
        const isAgency = ownerType === 'agency';
        const ownerLabel = isAgency
          ? (t('admin.cplOrders.ownerType.agency') || 'Агентство')
          : (t('admin.cplOrders.ownerType.creator') || 'Криейтор');

        return (
          <div className="flex items-center gap-2">
            <Typography
              variant="body2"
              className="!text-[#006FEE] cursor-pointer hover:underline"
              // @ts-ignore
              onClick={() => order.owner?.id && navigate(`/users/creators/${order.owner.id}/balance`)}
            >
              {/* @ts-ignore */}
              {order?.owner?.name || 'Unknown Partner'}
            </Typography>
            <span className={`inline-flex items-center h-[20px] px-[8px] rounded-lg text-[11px] font-medium ${
              isAgency
                ? 'bg-[#7828C8]/20 text-[#7828C8]'
                : 'bg-[#17C964]/20 text-[#17C964]'
            }`}>
              {ownerLabel}
            </span>
          </div>
        );

      case "statistics":
        // Statistics Cell: Progress bar with subscribers count
        return (
          <div className="flex flex-col gap-[8px] min-w-[180px]">
            <Progress
              classNames={{
                indicator: '!bg-[#006FEE]',
              }}
              color="primary"
              aria-label="Progress"
              className="max-w-md"
              value={order.subscribed_count || 0}
              maxValue={order?.subs?.count || 1}
            />
            <Typography variant="body3" className="text-[#11181C]">
              Подписчики: {order.subscribed_count || 0}<span className='text-[#71717A]'>/{order?.subs?.count}</span>
            </Typography>
          </div>
        );

      case "revenue":
        // Revenue Cell: Total revenue with platform commission percentage and amount
        //@ts-ignore
        const totalRevenue = parseFloat(order?.subscribed_count * (order?.subs?.price || 0)) || 0;

        // Helper function to safely convert commission to number
        const toNumber = (value: any): number => {
          if (typeof value === 'number' && Number.isFinite(value)) return value;
          if (typeof value === 'string') {
            const normalized = value.replace(',', '.');
            const parsed = Number.parseFloat(normalized);
            return Number.isFinite(parsed) ? parsed : 0;
          }
          return 0;
        };

        // Get commission percentage from API (handles both 0.15 and 15 formats)
        const rawCommission = toNumber(order.commition);
        const platformPercent = rawCommission <= 1 ? rawCommission * 100 : rawCommission;
        const platformCommission = totalRevenue * (platformPercent / 100);
        const netRevenue = totalRevenue - platformCommission;

        return (
          <div className="flex flex-col gap-[4px]">
            <Typography variant="body2" className="font-semibold text-[#11181C]">
              ${platformCommission.toFixed(2)} <span className="text-[#71717A] font-normal text-[12px]">({platformPercent.toFixed(0)}%)</span>
            </Typography>
            <Typography variant="body3" className="text-[#71717A]">
              ${totalRevenue.toFixed(2)}
            </Typography>
          </div>
        );

      case "status":
        // Status Cell: Badge with color-coded status
        return (
          <div className="flex items-center">
            <span className="inline-flex items-center h-[24px] px-[8px] py-[4px] gap-[4px] rounded-lg bg-gray-100">
              <div className={`w-2 h-2 rounded-full ${getStatusColor(order.status)}`}></div>
              <Typography variant="body3" className="text-gray-900">
                {getStatusLabel(order.status)}
              </Typography>
            </span>
          </div>
        );

      case "createdAt":
        // Date Cell: Shows time ago with tooltip
        return (
          <div className="flex flex-col gap-[8px]">
            <Tooltip
              content={(
                <div className='flex flex-col'>
                  <Typography variant='body3' className="text-[#71717A]">
                    {t('admin.cplOrders.createdAt')}:
                  </Typography>
                  <Typography variant='body2'>
                    {formatTimeAgo(order.created_at, t)}
                  </Typography>
                </div>
              )}
              placement="top"
            >
              <div className="flex items-center gap-[4px]">
                <CalendarIcon size={14} className="text-gray-600" />
                <Typography variant="body3" className="text-gray-600">
                  {formatTimeAgo(order.created_at, t)}
                </Typography>
              </div>
            </Tooltip>
          </div>
        );

      case "actions":
        // Actions Cell: Dropdown menu with deactivate option
        // Only show for non-deactivated/non-canceled orders
        if (order.status === 'deactivated' || order.status === 'canceled') {
          return null;
        }

        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="light"
                isIconOnly
                className="min-w-[32px] w-[32px] h-[32px]"
              >
                <Icon icon="solar:menu-dots-bold" width={20} height={20} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Действия"
              onAction={(key: React.Key) => {
                if (key === 'deactivate') {
                  onDeactivate?.(order._id);
                }
              }}
            >
              <DropdownItem key="deactivate" className="text-danger" color="danger">
                Деактивировать
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        );

      default:
        return null;
    }
  }, [t]);

  // Empty content component
  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <EmptyContentIc/>
        <div className="flex flex-col gap-[6px]">
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {t('admin.cplOrders.table.empty.title')}
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {t('admin.cplOrders.table.empty.description')}
          </Typography>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  // Add id field for UniversalTable compatibility
  // UniversalTable requires each item to have a unique 'id' field
  const ordersWithId = orders.map((order) => ({
    ...order,
    id: order._id // Map _id to id for table compatibility
  }));

  return (
    <UniversalTable
      data={ordersWithId}
      columns={columns}
      renderCell={renderCell}
      emptyContent={emptyContent}
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default CPLOrdersTable;
